/**
 * Practice Domain — Public API
 *
 * Single import surface for everything practice-related: the session hook,
 * the answer/validation contract, session-validation utilities, the runtime
 * UI components, and the pedagogical-guard telemetry that admin reads from.
 *
 * Grouping below mirrors the conceptual layers:
 *   - hooks/        → session orchestration
 *   - logic/        → pure protocols, schemas, guards, baselines, telemetry
 *   - components/   → runtime UI (rendered inside PracticeView)
 */

// ── Session Hook ─────────────────────────────────────────────
// Note: file is hooks/usePracticeSession.ts and the export now also reads
// as usePracticeSession (renamed from usePracticeHandlers in audit Phase C1).
export { usePracticeSession } from './hooks/usePracticeSession';

// ── Answer Protocol (types + conversion) ─────────────────────
export type { ChoicePayload, TextPayload, MappingPayload, OrderingPayload, CellSelectionPayload, KeyValuePayload, SegmentPayload, ValueSequencePayload, VisualDataPayload, TimePayload, TokenSetPayload, AnswerPayload, ValidationResult } from './logic/answerProtocol';
export { toAnswerPayload, fromAnswerPayload, parseAnswerPayload } from './logic/answerProtocol';

// ── Answer Payload Schemas (Zod runtime validation) ──────────
export { ChoicePayloadSchema, TextPayloadSchema, TimePayloadSchema, TokenSetPayloadSchema, MappingPayloadSchema, OrderingPayloadSchema, CellSelectionPayloadSchema, KeyValuePayloadSchema, SegmentPayloadSchema, ValueSequencePayloadSchema, VisualDataPayloadSchema, AnswerPayloadSchema, ValidationResultSchema, PayloadSchemaMap, validateAnswerPayload, validateValidationResult, validatePayloadByKind, isPayloadKind } from './logic/answerPayload';

// ── Session Validation ───────────────────────────────────────
export type { SessionValidationReport, SessionIntegritySnapshot } from './logic/sessionValidation';
export { validateSessionAvailability, logValidationReport, hasContentForGroup, createSessionSnapshot, validateSessionIntegrity } from './logic/sessionValidation';

// ── Question Time Baselines ──────────────────────────────────
export { getBaselineTimeMs, isFastResponse, normalizeResponseTime } from './logic/questionTimeBaselines';

// ── Pedagogical-guard telemetry (read-only consumer API) ─────
// Added in audit Phase A4: removes admin/SessionAnalyticsPage's deep import.
export { getGuardTelemetry } from './logic/guardTelemetry';

// ── Pedagogical Guard (assessment + future CLI consumers) ────
// Added in audit Phase A2: removes assessment/useAssessmentSession's deep
// import. The guard itself depends on `governance/checks/pedagogicalChecks`,
// so it stays in practice/ (which already depends on governance) — assessment
// imports the public API through this barrel instead of the file directly.
export { filterSession } from './logic/pedagogicalGuard';
export type { GuardStats, GuardVerdict } from './logic/pedagogicalGuard';

// ── UI Components ────────────────────────────────────────────
export { PracticeSession } from './components/PracticeSession';
export { PracticeErrorBoundary } from './components/PracticeErrorBoundary';
// Note: CheatSheetDrawer moved to support/components/ in RC Ronde 5 (S-3).
// Import via `from '../support'` barrel.
export { QuestionCardClean } from './components/QuestionCardClean';
export { SessionSummary } from './components/SessionSummary';
export { PracticeHeaderMinimal } from './components/PracticeHeaderMinimal';